import { forwardRef, useImperativeHandle, useState } from "react";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { BRAND, RED } from "@/shared/constants.js";
import { inputBase, labelBase } from "@/shared/utils/styles.js";

export const PaymentCardField = forwardRef(function PaymentCardField({ disabled, billingDetails, onCompleteChange, onErrorChange }, ref) {
  const stripe = useStripe();
  const elements = useElements();
  const [focusedField, setFocusedField] = useState(null);
  const [complete, setComplete] = useState({ number: false, expiry: false, cvc: false });
  const [errors, setErrors] = useState({ number: null, expiry: null, cvc: null });

  const stripeElementOptions = {
    disabled,
    style: {
      base: {
        color: "#1a1a1a",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: "14px",
        "::placeholder": { color: "#999" },
      },
      invalid: { color: RED },
    },
  };
  const stripeNumberOptions = { ...stripeElementOptions, showIcon: true };

  const stripeInputStyle = (field) => ({
    ...inputBase,
    padding: "13px 14px",
    borderColor: errors[field] ? RED : focusedField === field ? BRAND : "#d0d0d0",
    opacity: disabled ? 0.6 : 1,
  });

  const handleElementChange = (field) => (event) => {
    const message = event.error?.message || null;
    const nextComplete = { ...complete, [field]: Boolean(event.complete) };
    const nextErrors = { ...errors, [field]: message };

    setComplete(nextComplete);
    setErrors(nextErrors);
    onCompleteChange(Object.values(nextComplete).every(Boolean));
    onErrorChange(nextErrors.number || nextErrors.expiry || nextErrors.cvc);
  };

  useImperativeHandle(ref, () => ({
    async createPaymentMethod() {
      if (!stripe || !elements) throw new Error("Stripe is still loading. Please try again in a moment.");
      const card = elements.getElement(CardNumberElement);
      if (!card) throw new Error("Card entry is not ready. Please refresh and try again.");
      const result = await stripe.createPaymentMethod({
        type: "card",
        card,
        billing_details: billingDetails,
      });
      if (result.error) throw new Error(result.error.message || "Payment method could not be created.");
      return result.paymentMethod;
    },
    async confirmCardPayment(clientSecret) {
      if (!stripe) throw new Error("Stripe is still loading. Please try again in a moment.");
      const result = await stripe.confirmCardPayment(clientSecret);
      if (result.error) throw new Error(result.error.message || "Payment confirmation failed.");
      return result.paymentIntent;
    },
  }), [stripe, elements, billingDetails]);

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={stripeInputStyle("number")}>
        <CardNumberElement
          onFocus={() => setFocusedField("number")}
          onBlur={() => setFocusedField(null)}
          onChange={handleElementChange("number")}
          options={stripeNumberOptions}
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <label style={{ ...labelBase, marginBottom: 5 }}>Expiration date</label>
          <div style={stripeInputStyle("expiry")}>
            <CardExpiryElement
              onFocus={() => setFocusedField("expiry")}
              onBlur={() => setFocusedField(null)}
              onChange={handleElementChange("expiry")}
              options={stripeElementOptions}
            />
          </div>
        </div>
        <div>
          <label style={{ ...labelBase, marginBottom: 5 }}>CVV</label>
          <div style={stripeInputStyle("cvc")}>
            <CardCvcElement
              onFocus={() => setFocusedField("cvc")}
              onBlur={() => setFocusedField(null)}
              onChange={handleElementChange("cvc")}
              options={stripeElementOptions}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
